import Service, { service } from '@ember/service';
import { action } from '@ember/object';
import type StoreService from '@ember-data/store';
import type { UploadFile } from 'ember-file-upload';
import type PersonModel from '../models/person.ts';
import type ObservableDocumentModel from '../models/observable-document';
import type DigitalAssetsService from './digital-assets';

export default class ObservablesService extends Service {
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;

  @action
  async createObservableDocument(
    uploadFile: UploadFile,
    options: { person: PersonModel; batch?: string; via?: string }
  ): Promise<ObservableDocumentModel> {
    const { person, batch, via = 'UPLOAD' } = options;

    // get API path for observable assets
    const collectionPath = this.store
      // @ts-expect-error: As we replace with request manager these will be resolved
      .adapterFor('application')
      .urlForCreateRecord('observable-asset');

    // set headers
    // @ts-expect-error: As we replace with request manager these will be resolved
    const headers = this.store.adapterFor('application')?.headers;
    headers['Content-Type'] = 'application/vnd.api+json';

    // create asset record through API
    const assetResponse = await fetch(`${collectionPath}/s3-signed-url`, {
      headers: headers,
      method: 'POST',
      body: JSON.stringify({ filename: uploadFile.file.name, public_image: false }),
    });

    // read asset response content and upload binary to signed url
    const assetDetails = await assetResponse.json();

    // set asset detail classification if created via proxy; backend will derive this
    // automatically for txt or pdf files, but admin users creating an observable
    // document by proxy should be able to provide an image file when appropriate
    if (via === 'PROXY') assetDetails.classification = 'ObservableProvider.PROXY';
    await uploadFile.uploadBinary(assetDetails.url, { method: 'PUT' });

    // create and save observable asset
    const observableAsset = this.digitalAssets.createAssetFromS3Details(
      'observable-asset',
      assetDetails
    );
    await observableAsset.save();

    const contentType = observableAsset.contentType;
    const provider = observableAsset.classification;

    // create and save observable document
    const observableDocument = this.store.createRecord('observable-document', {
      observableAsset,
      person,
      batch,
      contentType,
      provider,
      via,
    }) as ObservableDocumentModel;
    await observableDocument.save();

    return observableDocument;
  }

  @action
  updateObservableDocument(attributes: Record<string, unknown>): ObservableDocumentModel {
    // @ts-expect-error: As we replace with request manager these will be resolved
    return this.store.push({
      data: [
        {
          type: 'observable-document',
          // @ts-expect-error: As we replace with request manager these will be resolved
          id: attributes.id,
          // @ts-expect-error: As we replace with request manager these will be resolved
          attributes,
        },
      ],
    });
  }
}
